import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/fr";
import "dayjs/locale/es";
import "dayjs/locale/ar";
import * as EN from "./en.json"
import * as ES from "./es.json"
import * as AR from "./ar.json"
import * as FR from "./fr.json"

// Feature namespaces live in their own files and are merged in below.
// Adding keys to the four shared language files means every branch that adds
// UI text edits the same four files; a namespace per feature keeps those
// branches from colliding over translations.
import * as INSIGHTS_EN from "./insights/en.json"
import * as INSIGHTS_FR from "./insights/fr.json"
import * as INSIGHTS_ES from "./insights/es.json"
import * as INSIGHTS_AR from "./insights/ar.json"
import * as WORK_EN from "./work/en.json"
import * as WORK_FR from "./work/fr.json"
import * as WORK_ES from "./work/es.json"
import * as WORK_AR from "./work/ar.json"
import * as SEATS_EN from "./seats/en.json"
import * as SEATS_FR from "./seats/fr.json"
import * as SEATS_ES from "./seats/es.json"
import * as SEATS_AR from "./seats/ar.json"
import * as SUPPORT_EN from "./support/en.json"
import * as SUPPORT_FR from "./support/fr.json"
import * as SUPPORT_ES from "./support/es.json"
import * as SUPPORT_AR from "./support/ar.json"
import * as TEAM_EN from "./team/en.json"
import * as TEAM_FR from "./team/fr.json"
import * as TEAM_ES from "./team/es.json"
import * as TEAM_AR from "./team/ar.json"
import * as AGENTS_EN from "./agents/en.json"
import * as AGENTS_FR from "./agents/fr.json"
import * as AGENTS_ES from "./agents/es.json"
import * as AGENTS_AR from "./agents/ar.json"
import * as AUTH_EN from "./auth/en.json"
import * as AUTH_FR from "./auth/fr.json"
import * as AUTH_ES from "./auth/es.json"
import * as AUTH_AR from "./auth/ar.json"
import * as PRESENCE_EN from "./presence/en.json"
import * as PRESENCE_FR from "./presence/fr.json"
import * as PRESENCE_ES from "./presence/es.json"
import * as PRESENCE_AR from "./presence/ar.json"
import * as ACCOUNT_EN from "./account/en.json"
import * as ACCOUNT_FR from "./account/fr.json"
import * as ACCOUNT_ES from "./account/es.json"
import * as ACCOUNT_AR from "./account/ar.json"
import * as VENDOR_TEAM_EN from "./vendor-team/en.json"
import * as VENDOR_TEAM_FR from "./vendor-team/fr.json"
import * as VENDOR_TEAM_ES from "./vendor-team/es.json"
import * as VENDOR_TEAM_AR from "./vendor-team/ar.json"
import * as SUPPORT_ACCESS_EN from "./support-access/en.json"
import * as SUPPORT_ACCESS_FR from "./support-access/fr.json"
import * as SUPPORT_ACCESS_ES from "./support-access/es.json"
import * as SUPPORT_ACCESS_AR from "./support-access/ar.json"
import * as ENVIRONMENT_EN from "./environment/en.json"
import * as ENVIRONMENT_FR from "./environment/fr.json"
import * as ENVIRONMENT_ES from "./environment/es.json"
import * as ENVIRONMENT_AR from "./environment/ar.json"
// feat/room-to-revenue namespaces — docs/room-to-revenue.md
import * as R2R_NS_EN from "./r2r/en.json"
import * as R2R_NS_FR from "./r2r/fr.json"
import * as R2R_NS_ES from "./r2r/es.json"
import * as R2R_NS_AR from "./r2r/ar.json"
import * as PRODUCT_PRICING_NS_EN from "./product-pricing/en.json"
import * as PRODUCT_PRICING_NS_FR from "./product-pricing/fr.json"
import * as PRODUCT_PRICING_NS_ES from "./product-pricing/es.json"
import * as PRODUCT_PRICING_NS_AR from "./product-pricing/ar.json"
import * as SHOPPER_REQUESTS_NS_EN from "./shopper-requests/en.json"
import * as SHOPPER_REQUESTS_NS_FR from "./shopper-requests/fr.json"
import * as SHOPPER_REQUESTS_NS_ES from "./shopper-requests/es.json"
import * as SHOPPER_REQUESTS_NS_AR from "./shopper-requests/ar.json"
import * as LEAD_DESK_NS_EN from "./lead-desk/en.json"
import * as LEAD_DESK_NS_FR from "./lead-desk/fr.json"
import * as LEAD_DESK_NS_ES from "./lead-desk/es.json"
import * as LEAD_DESK_NS_AR from "./lead-desk/ar.json"
import * as CONTENT_PACKS_NS_EN from "./content-packs/en.json"
import * as CONTENT_PACKS_NS_FR from "./content-packs/fr.json"
import * as CONTENT_PACKS_NS_ES from "./content-packs/es.json"
import * as CONTENT_PACKS_NS_AR from "./content-packs/ar.json"
import * as DECISIONS_NS_EN from "./decisions/en.json"
import * as DECISIONS_NS_FR from "./decisions/fr.json"
import * as DECISIONS_NS_ES from "./decisions/es.json"
import * as DECISIONS_NS_AR from "./decisions/ar.json"
import * as AI_CHANNELS_NS_EN from "./ai-channels/en.json"
import * as AI_CHANNELS_NS_FR from "./ai-channels/fr.json"
import * as AI_CHANNELS_NS_ES from "./ai-channels/es.json"
import * as AI_CHANNELS_NS_AR from "./ai-channels/ar.json"
import * as TRADE_NS_EN from "./trade/en.json"
import * as TRADE_NS_FR from "./trade/fr.json"
import * as TRADE_NS_ES from "./trade/es.json"
import * as TRADE_NS_AR from "./trade/ar.json"
import * as SAVED_ROOMS_NS_EN from "./saved-rooms/en.json"
import * as SAVED_ROOMS_NS_FR from "./saved-rooms/fr.json"
import * as SAVED_ROOMS_NS_ES from "./saved-rooms/es.json"
import * as SAVED_ROOMS_NS_AR from "./saved-rooms/ar.json"
// The Ardealy Network, retailer side (Products → Network)
import * as NETWORK_RETAILER_NS_EN from "./network-retailer/en.json"
import * as NETWORK_RETAILER_NS_FR from "./network-retailer/fr.json"
import * as NETWORK_RETAILER_NS_ES from "./network-retailer/es.json"
import * as NETWORK_RETAILER_NS_AR from "./network-retailer/ar.json"
import * as UNIT_ECONOMICS_NS_EN from "./unit-economics/en.json"
import * as UNIT_ECONOMICS_NS_FR from "./unit-economics/fr.json"
import * as UNIT_ECONOMICS_NS_ES from "./unit-economics/es.json"
import * as UNIT_ECONOMICS_NS_AR from "./unit-economics/ar.json"
import * as ARDI_EN from "./ardi/en.json"
import * as ARDI_FR from "./ardi/fr.json"
import * as ARDI_ES from "./ardi/es.json"
import * as ARDI_AR from "./ardi/ar.json"
import * as APP_UPDATE_EN from "./app-update/en.json"
import * as APP_UPDATE_FR from "./app-update/fr.json"
import * as APP_UPDATE_ES from "./app-update/es.json"
import * as APP_UPDATE_AR from "./app-update/ar.json"
import * as PUSH_EN from "./push/en.json"
import * as VIDEO_EDITOR_EN from "./video-editor/en.json"
import * as VIDEO_EDITOR_FR from "./video-editor/fr.json"
import * as VIDEO_EDITOR_ES from "./video-editor/es.json"
import * as VIDEO_EDITOR_AR from "./video-editor/ar.json"
import * as STUDIO_NS_EN from "./studio/en.json"
import * as STUDIO_NS_FR from "./studio/fr.json"
import * as STUDIO_NS_ES from "./studio/es.json"
import * as STUDIO_NS_AR from "./studio/ar.json"
import * as PINTEREST_NS_EN from "./pinterest/en.json"
import * as PINTEREST_NS_FR from "./pinterest/fr.json"
import * as PINTEREST_NS_ES from "./pinterest/es.json"
import * as PINTEREST_NS_AR from "./pinterest/ar.json"
import * as PUSH_FR from "./push/fr.json"
import * as PUSH_ES from "./push/es.json"
import * as PUSH_AR from "./push/ar.json"
import * as ANALYTICS_KIT_NS_EN from "./analytics-kit/en.json"
import * as ANALYTICS_KIT_NS_FR from "./analytics-kit/fr.json"
import * as ANALYTICS_KIT_NS_ES from "./analytics-kit/es.json"
import * as ANALYTICS_KIT_NS_AR from "./analytics-kit/ar.json"
import * as VIZ_ANALYTICS_NS_EN from "./viz-analytics/en.json"
import * as VIZ_ANALYTICS_NS_FR from "./viz-analytics/fr.json"
import * as VIZ_ANALYTICS_NS_ES from "./viz-analytics/es.json"
import * as VIZ_ANALYTICS_NS_AR from "./viz-analytics/ar.json"
import * as BUSINESS_NS_EN from "./business/en.json"
import * as BUSINESS_NS_FR from "./business/fr.json"
import * as BUSINESS_NS_ES from "./business/es.json"
import * as BUSINESS_NS_AR from "./business/ar.json"
import * as SHOPPER_UI_NS_EN from "./shopper-ui/en.json"
import * as SHOPPER_UI_NS_FR from "./shopper-ui/fr.json"
import * as SHOPPER_UI_NS_ES from "./shopper-ui/es.json"
import * as SHOPPER_UI_NS_AR from "./shopper-ui/ar.json"
import * as SHOPPER_TEAM_NS_EN from "./shopper-team/en.json"
import * as SHOPPER_TEAM_NS_FR from "./shopper-team/fr.json"
import * as SHOPPER_TEAM_NS_ES from "./shopper-team/es.json"
import * as SHOPPER_TEAM_NS_AR from "./shopper-team/ar.json"
import * as HOME_OVERVIEW_NS_EN from "./home-overview/en.json"
import * as HOME_OVERVIEW_NS_FR from "./home-overview/fr.json"
import * as HOME_OVERVIEW_NS_ES from "./home-overview/es.json"
import * as HOME_OVERVIEW_NS_AR from "./home-overview/ar.json"
import * as ONBOARDING_V2_NS_EN from "./onboarding-v2/en.json"
import * as ONBOARDING_V2_NS_FR from "./onboarding-v2/fr.json"
import * as ONBOARDING_V2_NS_ES from "./onboarding-v2/es.json"
import * as ONBOARDING_V2_NS_AR from "./onboarding-v2/ar.json"
import * as FREE_PLAN_NS_EN from "./free-plan/en.json"
import * as FREE_PLAN_NS_FR from "./free-plan/fr.json"
import * as FREE_PLAN_NS_ES from "./free-plan/es.json"
import * as FREE_PLAN_NS_AR from "./free-plan/ar.json"
import * as SIGNUP_NS_EN from "./signup/en.json"
import * as SIGNUP_NS_FR from "./signup/fr.json"
import * as SIGNUP_NS_ES from "./signup/es.json"
import * as SIGNUP_NS_AR from "./signup/ar.json"
import * as DESIGN_EDITOR_NS_EN from "./design-editor/en.json"
import * as DESIGN_EDITOR_NS_FR from "./design-editor/fr.json"
import * as DESIGN_EDITOR_NS_ES from "./design-editor/es.json"
import * as DESIGN_EDITOR_NS_AR from "./design-editor/ar.json"
import * as PRICING_NS_EN from "./pricing/en.json"
import * as PRICING_NS_FR from "./pricing/fr.json"
import * as PRICING_NS_ES from "./pricing/es.json"
import * as PRICING_NS_AR from "./pricing/ar.json"
import { ADMIN_TRANSLATIONS } from "./admin"

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .use(LanguageDetector)
  .init({
    resources: {
      en: {
        translation: { ...(EN as any), ...(INSIGHTS_EN as any), ...(WORK_EN as any), ...(SEATS_EN as any), ...(SUPPORT_EN as any), ...(TEAM_EN as any), ...(AGENTS_EN as any), ...(AUTH_EN as any), ...(PRESENCE_EN as any), ...(ACCOUNT_EN as any), ...(VENDOR_TEAM_EN as any), ...(SUPPORT_ACCESS_EN as any), ...(ENVIRONMENT_EN as any), ...(ARDI_EN as any), ...(APP_UPDATE_EN as any), ...(PUSH_EN as any), ...(VIDEO_EDITOR_EN as any), ...(STUDIO_NS_EN as any), ...(PINTEREST_NS_EN as any), ...(ANALYTICS_KIT_NS_EN as any), ...(VIZ_ANALYTICS_NS_EN as any), ...(BUSINESS_NS_EN as any), ...(SHOPPER_UI_NS_EN as any), ...(SHOPPER_TEAM_NS_EN as any), ...(HOME_OVERVIEW_NS_EN as any), ...(ONBOARDING_V2_NS_EN as any), ...(FREE_PLAN_NS_EN as any), ...(PRICING_NS_EN as any), ...(SIGNUP_NS_EN as any), ...(DESIGN_EDITOR_NS_EN as any), ...(R2R_NS_EN as any), ...(PRODUCT_PRICING_NS_EN as any), ...(SHOPPER_REQUESTS_NS_EN as any), ...(LEAD_DESK_NS_EN as any), ...(CONTENT_PACKS_NS_EN as any), ...(DECISIONS_NS_EN as any), ...(AI_CHANNELS_NS_EN as any), ...(TRADE_NS_EN as any), ...(SAVED_ROOMS_NS_EN as any), ...(NETWORK_RETAILER_NS_EN as any), ...(UNIT_ECONOMICS_NS_EN as any), ...ADMIN_TRANSLATIONS.en },
      },
      fr: {
        translation: { ...(FR as any), ...(INSIGHTS_FR as any), ...(WORK_FR as any), ...(SEATS_FR as any), ...(SUPPORT_FR as any), ...(TEAM_FR as any), ...(AGENTS_FR as any), ...(AUTH_FR as any), ...(PRESENCE_FR as any), ...(ACCOUNT_FR as any), ...(VENDOR_TEAM_FR as any), ...(SUPPORT_ACCESS_FR as any), ...(ENVIRONMENT_FR as any), ...(ARDI_FR as any), ...(APP_UPDATE_FR as any), ...(PUSH_FR as any), ...(VIDEO_EDITOR_FR as any), ...(STUDIO_NS_FR as any), ...(PINTEREST_NS_FR as any), ...(ANALYTICS_KIT_NS_FR as any), ...(VIZ_ANALYTICS_NS_FR as any), ...(BUSINESS_NS_FR as any), ...(SHOPPER_UI_NS_FR as any), ...(SHOPPER_TEAM_NS_FR as any), ...(HOME_OVERVIEW_NS_FR as any), ...(ONBOARDING_V2_NS_FR as any), ...(FREE_PLAN_NS_FR as any), ...(PRICING_NS_FR as any), ...(SIGNUP_NS_FR as any), ...(DESIGN_EDITOR_NS_FR as any), ...(R2R_NS_FR as any), ...(PRODUCT_PRICING_NS_FR as any), ...(SHOPPER_REQUESTS_NS_FR as any), ...(LEAD_DESK_NS_FR as any), ...(CONTENT_PACKS_NS_FR as any), ...(DECISIONS_NS_FR as any), ...(AI_CHANNELS_NS_FR as any), ...(TRADE_NS_FR as any), ...(SAVED_ROOMS_NS_FR as any), ...(NETWORK_RETAILER_NS_FR as any), ...(UNIT_ECONOMICS_NS_FR as any), ...ADMIN_TRANSLATIONS.fr },
      },
      ar: {
        translation: { ...(AR as any), ...(INSIGHTS_AR as any), ...(WORK_AR as any), ...(SEATS_AR as any), ...(SUPPORT_AR as any), ...(TEAM_AR as any), ...(AGENTS_AR as any), ...(AUTH_AR as any), ...(PRESENCE_AR as any), ...(ACCOUNT_AR as any), ...(VENDOR_TEAM_AR as any), ...(SUPPORT_ACCESS_AR as any), ...(ENVIRONMENT_AR as any), ...(ARDI_AR as any), ...(APP_UPDATE_AR as any), ...(PUSH_AR as any), ...(VIDEO_EDITOR_AR as any), ...(STUDIO_NS_AR as any), ...(PINTEREST_NS_AR as any), ...(ANALYTICS_KIT_NS_AR as any), ...(VIZ_ANALYTICS_NS_AR as any), ...(BUSINESS_NS_AR as any), ...(SHOPPER_UI_NS_AR as any), ...(SHOPPER_TEAM_NS_AR as any), ...(HOME_OVERVIEW_NS_AR as any), ...(ONBOARDING_V2_NS_AR as any), ...(FREE_PLAN_NS_AR as any), ...(PRICING_NS_AR as any), ...(SIGNUP_NS_AR as any), ...(DESIGN_EDITOR_NS_AR as any), ...(R2R_NS_AR as any), ...(PRODUCT_PRICING_NS_AR as any), ...(SHOPPER_REQUESTS_NS_AR as any), ...(LEAD_DESK_NS_AR as any), ...(CONTENT_PACKS_NS_AR as any), ...(DECISIONS_NS_AR as any), ...(AI_CHANNELS_NS_AR as any), ...(TRADE_NS_AR as any), ...(SAVED_ROOMS_NS_AR as any), ...(NETWORK_RETAILER_NS_AR as any), ...(UNIT_ECONOMICS_NS_AR as any), ...ADMIN_TRANSLATIONS.ar },
      },
      es: {
        translation: { ...(ES as any), ...(INSIGHTS_ES as any), ...(WORK_ES as any), ...(SEATS_ES as any), ...(SUPPORT_ES as any), ...(TEAM_ES as any), ...(AGENTS_ES as any), ...(AUTH_ES as any), ...(PRESENCE_ES as any), ...(ACCOUNT_ES as any), ...(VENDOR_TEAM_ES as any), ...(SUPPORT_ACCESS_ES as any), ...(ENVIRONMENT_ES as any), ...(ARDI_ES as any), ...(APP_UPDATE_ES as any), ...(PUSH_ES as any), ...(VIDEO_EDITOR_ES as any), ...(STUDIO_NS_ES as any), ...(PINTEREST_NS_ES as any), ...(ANALYTICS_KIT_NS_ES as any), ...(VIZ_ANALYTICS_NS_ES as any), ...(BUSINESS_NS_ES as any), ...(SHOPPER_UI_NS_ES as any), ...(SHOPPER_TEAM_NS_ES as any), ...(HOME_OVERVIEW_NS_ES as any), ...(ONBOARDING_V2_NS_ES as any), ...(FREE_PLAN_NS_ES as any), ...(PRICING_NS_ES as any), ...(SIGNUP_NS_ES as any), ...(DESIGN_EDITOR_NS_ES as any), ...(R2R_NS_ES as any), ...(PRODUCT_PRICING_NS_ES as any), ...(SHOPPER_REQUESTS_NS_ES as any), ...(LEAD_DESK_NS_ES as any), ...(CONTENT_PACKS_NS_ES as any), ...(DECISIONS_NS_ES as any), ...(AI_CHANNELS_NS_ES as any), ...(TRADE_NS_ES as any), ...(SAVED_ROOMS_NS_ES as any), ...(NETWORK_RETAILER_NS_ES as any), ...(UNIT_ECONOMICS_NS_ES as any), ...ADMIN_TRANSLATIONS.es },
      }
    },
    lng: "en",
    fallbackLng: "en",
    interpolation: {
      escapeValue: false,
    },
  });

// Keep dayjs locale in sync with i18next so date/time formats (MMM D, dddd, fromNow…) follow the active language
const syncDayjsLocale = (lng: string) => {
  const supported = ['en', 'fr', 'es', 'ar'];
  dayjs.locale(supported.includes(lng) ? lng : 'en');
};
syncDayjsLocale(i18n.language || 'en');
i18n.on('languageChanged', syncDayjsLocale);